import React, { useEffect, useState } from "react";
import { Input, Popover } from "antd";
import { ItemWrapper } from "./Elements";
import { IconButton } from "@mui/material";
import {
  CircleOutlined,
  Create,
  MoreVert,
  Visibility,
  Delete,
  PowerSettingsNew,
  Close,
  ReplayOutlined,
  CheckCircleOutlined,
  CurrencyExchangeOutlined,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { Modal } from "antd";
import {
  ACTIVATE,
  APPROVE,
  CLOSE,
  DEACTIVATE,
  DELETE,
  EDIT,
  REACTIVATE,
  REJECT,
  RENEW,
  REQUEST_ADVANCE,
  REQUEST_REQUISITION,
  REQUEST_REVERSE,
  VIEW,
} from "../../utils/constants";
import useSet from "../../hooks/common/useSet";
import { useToasts } from "../../hooks/common/useToasts";
import TextArea from "antd/es/input/TextArea";
import SelectInput from "./SelectInput";
import useGet from "../../hooks/common/useGet";
import { API_STAFF_TYPE } from "../../utils/apis";
import { useUserOptions } from "../../hooks/assets/assetUtils";

type TActionProps = {
  label: string;
  to?: string;
  state?: any;
  icon?: any;
  onClick?: () => void;
  deleteMessage?: string;
  deleteUrl?: string;
  custom?: boolean;
  handleCustom?: (reason?: string) => void;
  showReasonField?: boolean;
  reasonFieldLabel?: string;
  apiMethod?: string;
  method?: "PATCH" | "POST" | "PUT" | "DELETE";
  apiParams?: any;
  fields?: Record<string, any>;
  actionType?: "clamp" | "unclamp" | "custom";
  modalTitle?: string;
  modalContent?:
    | React.ReactNode
    | ((setData: (data: any) => void) => React.ReactNode);
  modalOkText?: string;
  modalOkType?: "primary" | "danger" | "dashed" | "link" | "text" | "default";
  modalCancelText?: string;
  modalWidth?: number;
  onModalOk?: (data?: any) => void | Promise<void>;
  fieldName?: string;
};
type TFormActionsProps = {
  actions: TActionProps[];
  serviceOptions?: { value: string; label: string }[];
};

function getIcon(label: string) {
  switch (label) {
    case VIEW:
      return <Visibility fontSize="small" />;
    case EDIT:
      return <Create fontSize="small" />;
    case RENEW:
      return <ReplayOutlined fontSize="small" />;
    case REQUEST_REVERSE:
      return <ReplayOutlined fontSize="small" color="info" />;
    case DELETE:
      return <Delete fontSize="small" color="error" />;
    case ACTIVATE:
      return <PowerSettingsNew fontSize="small" color="success" />;
    case APPROVE:
      return <CheckCircleOutlined fontSize="small" color="success" />;
    case REACTIVATE:
      return <PowerSettingsNew fontSize="small" color="success" />;
    case REQUEST_ADVANCE:
      return <CurrencyExchangeOutlined fontSize="small" color="success" />;
    case DEACTIVATE:
      return <PowerSettingsNew fontSize="small" color="error" />;
    case REJECT:
      return <PowerSettingsNew fontSize="small" color="error" />;
    case CLOSE:
      return <Close fontSize="small" color="warning" />;
    case REQUEST_REQUISITION:
      return <ReplayOutlined fontSize="small" color="warning" />;
    default:
      return <CircleOutlined sx={{ fontSize: 8, width: 20 }} />;
  }
}

const handleApprove = (
  approveUrl: string,
  approveMessage: string,
  onApprove: any,
  showTextField: boolean,
  fieldName: string = "checker_notes",
  method: "PATCH" | "POST" | "PUT" | "DELETE" = "PATCH",
) => {
  let fieldValue = "";
  Modal.confirm({
    title: "Confirm Approve",
    content: (
      <div>
        <p>{approveMessage}</p>
        {showTextField && (
          <div>
            <label>Provide Checker Notes</label>
            <TextArea
              placeholder="Enter checker notes"
              onChange={(e) => (fieldValue = e.target.value)}
              style={{ marginTop: "8px" }}
            />
          </div>
        )}
      </div>
    ),
    okText: "Yes, Approve",
    cancelText: "Cancel",
    onOk: async () => {
      onApprove({
        api: approveUrl,
        method,
        params: {
          [fieldName]: fieldValue,
          status: "APPROVED",
        },
      });
    },
  });
};

const handleRequestReversal = (
  reversalUrl: string,
  reversalMessage: string,
  onReverse: any,
  showTextField: boolean,
) => {
  const formData = { amount: "", reversalReason: "" };

  Modal.confirm({
    title: "Confirm Request Reversal",
    content: (
      <div>
        <p>{reversalMessage}</p>
        {showTextField && (
          <>
            <div>
              <label>Amount Received </label>
              <Input
                placeholder="Enter amount received from customer"
                type="number"
                onChange={(e) => (formData.amount = e.target.value)}
                style={{ marginTop: "8px" }}
              />
            </div>
            <div>
              <label>Provide Reversal Reason </label>
              <TextArea
                placeholder="Enter reversal reason"
                onChange={(e) => (formData.reversalReason = e.target.value)}
                style={{ marginTop: "8px" }}
              />
            </div>
          </>
        )}
      </div>
    ),
    okText: "Yes, Request Reversal",
    cancelText: "Cancel",
    onOk: async () => {
      onReverse({
        api: reversalUrl,
        method: "POST",
        params: {
          reason: formData.reversalReason,
          amount_received: Number(formData.amount),
        },
      });
    },
  });
};

const handleReject = (
  rejectUrl: string,
  rejectMessage: string,
  onReject: any,
  showTextField: boolean,
  fieldName: string = "reason",
) => {
  let fieldValue = "";
  Modal.confirm({
    title: "Confirm Reject",
    content: (
      <div>
        <p>{rejectMessage}</p>
        {showTextField && (
          <div>
            <label>Provide Rejection Reason </label>
            <TextArea
              placeholder="Enter rejection reason"
              required
              onChange={(e) => (fieldValue = e.target.value)}
              style={{ marginTop: "8px" }}
            />
          </div>
        )}
      </div>
    ),
    okText: "Yes, Reject",
    cancelText: "Cancel",
    onOk: async () => {
      onReject({
        api: rejectUrl,
        method: "POST",
        params: {
          [fieldName]: fieldValue,
        },
      });
    },
  });
};

const handleDelete = (
  deleteUrl: string,
  deleteMessage: string,
  onDelete: any,
) => {
  Modal.confirm({
    title: "Confirm Delete",
    content: deleteMessage,
    okText: "Yes, Delete",
    okType: "danger",
    cancelText: "Cancel",
    onOk: async () => {
      onDelete({ api: deleteUrl, method: "DELETE" });
    },
  });
};

const handleDeactivate = (
  deleteUrl: string,
  deleteMessage: string,
  onDelete: any,
  action: any = {},
) => {
  const { fields = {}, showReasonField = false, method } = action;
  let deactivationReason = "";

  Modal.confirm({
    title: "Confirm Deactivate",
    content: (
      <div>
        <p>{deleteMessage}</p>
        {showReasonField && (
          <div>
            <label>Comment</label>
            <TextArea
              placeholder="Enter reason for deactivation"
              onChange={(e) => (deactivationReason = e.target.value)}
              style={{ marginTop: "8px" }}
            />
          </div>
        )}
      </div>
    ),
    okText: "Yes, Deactivate",
    okType: "danger",
    cancelText: "Cancel",
    onOk: async () => {
      const params = {
        ...fields,
      };

      if (showReasonField && deactivationReason) {
        params.deactivation_reason = deactivationReason;
      }

      onDelete({
        api: deleteUrl,
        method,
        params,
      });
    },
  });
};

const handleReactivate = (
  deleteUrl: string,
  deleteMessage: string,
  onDelete: any,
) => {
  Modal.confirm({
    title: "Confirm Reactivate",
    content: deleteMessage,
    okText: "Yes, Reactivate",
    okType: "danger",
    cancelText: "Cancel",
    onOk: async () => {
      onDelete({
        api: deleteUrl,
        method: "POST",
      });
    },
  });
};

const handleActivate = (
  deleteUrl: string,
  deleteMessage: string,
  onDelete: any,
) => {
  Modal.confirm({
    title: "Confirm Activate",
    content: deleteMessage,
    okText: "Yes, Activate",
    cancelText: "Cancel",
    onOk: async () => {
      onDelete({
        api: deleteUrl,
        method: "PATCH",
        params: { active: true },
      });
    },
  });
};

const handleClose = (
  deleteUrl: string,
  deleteMessage: string,
  onDelete: any,
) => {
  Modal.confirm({
    title: "Confirm Close",
    content: deleteMessage,
    okText: "Yes, Close",
    okType: "danger",
    cancelText: "Cancel",
    onOk: async () => {
      onDelete({ api: deleteUrl, method: "POST" });
    },
  });
};

// Component to manage clamp modal state
const ClampModalContent = ({
  serviceOptions,
  onServiceChange,
}: {
  serviceOptions: { value: string; label: string }[];
  onServiceChange: (value: string) => void;
}) => {
  const [selectedService, setSelectedService] = useState(
    serviceOptions[0]?.value || "",
  );

  const handleChange = (value: any) => {
    setSelectedService(value);
    onServiceChange(value);
  };

  return (
    <div>
      <p>Are you sure you want to clamp this vehicle?</p>
      <div style={{ marginTop: "16px" }}>
        <label style={{ display: "block", marginBottom: "8px" }}>
          Reason for Clamping:
        </label>
        <SelectInput
          name="service_type"
          options={serviceOptions}
          onChange={handleChange}
          value={selectedService}
          placeholder="Select service type"
        />
      </div>
    </div>
  );
};

const handleClampAction = (
  actionUrl: string,
  onAction: any,
  apiMethod: string,
  apiParams?: any,
  serviceOptions: { value: string; label: string }[] = [],
) => {
  let selectedServiceId = serviceOptions[0]?.value || "";

  Modal.confirm({
    title: "Confirm Clamp",
    content: (
      <ClampModalContent
        serviceOptions={serviceOptions}
        onServiceChange={(value: string) => {
          selectedServiceId = value;
        }}
      />
    ),
    okText: "Yes, Clamp",
    cancelText: "Cancel",
    onOk: async () => {
      const params = selectedServiceId
        ? { ...apiParams, service_type: selectedServiceId }
        : apiParams;
      onAction({
        api: actionUrl,
        method: apiMethod,
        params,
      });
    },
  });
};

const handleUnclampAction = (
  actionUrl: string,
  onAction: any,
  apiMethod: string,
  apiParams?: any,
) => {
  Modal.confirm({
    title: "Confirm Unclamp",
    content: (
      <div>
        <p>Are you sure you want to unclamp this vehicle?</p>
      </div>
    ),
    okText: "Yes, Unclamp",
    cancelText: "Cancel",
    onOk: async () => {
      onAction({
        api: actionUrl,
        method: apiMethod,
        params: apiParams,
      });
    },
  });
};

const REQUISITION_STATUS_PENDING = "PENDING";

const RequestRequisitionModalContent = ({
  deleteMessage,
  initialParams,
  form,
  onFieldChange,
}: {
  deleteMessage: string;
  initialParams: {
    reference_number?: string;
    staff_type?: string;
    total_amount?: string | number;
  };
  form: {
    staff_type: string;
    total_amount: string | number;
    requested_by: string;
    action_comment: string;
  };
  onFieldChange: (field: keyof typeof form, value: any) => void;
}) => {
  const {
    loadData: getStaffTypes,
    data: staffTypesData,
    loading: loadingStaffTypes,
  } = useGet();

  const requestedByOptions = useUserOptions(
    form.requested_by ? { id: form.requested_by } : undefined,
  );

  useEffect(() => {
    getStaffTypes({ api: API_STAFF_TYPE });
  }, []);

  const staffTypes = Array.isArray(staffTypesData)
    ? staffTypesData
    : staffTypesData?.results || [];

  useEffect(() => {
    if (!form.staff_type && staffTypes.length && initialParams?.staff_type) {
      const match = staffTypes.find(
        (type: any) =>
          type.id === initialParams.staff_type ||
          type.code === initialParams.staff_type ||
          type.name === initialParams.staff_type,
      );
      if (match) onFieldChange("staff_type", match.id);
    }
  }, [staffTypes]);

  return (
    <div>
      <p>{deleteMessage}</p>
      <div style={{ marginTop: "8px", marginBottom: "12px" }}>
        <p style={{ margin: 0 }}>
          <strong>Reference Number:</strong> {initialParams?.reference_number}
        </p>
      </div>

      <div style={{ marginBottom: "12px" }}>
        <label style={{ display: "block", marginBottom: "8px" }}>
          Staff Type
        </label>
        <SelectInput
          name="staff_type"
          options={staffTypes.map((type: any) => ({
            label: type.name,
            value: type.id,
          }))}
          loading={loadingStaffTypes}
          value={form.staff_type}
          onChange={(value) => onFieldChange("staff_type", value)}
          placeholder="Select staff type"
        />
      </div>

      <div style={{ marginBottom: "12px" }}>
        <label style={{ display: "block", marginBottom: "8px" }}>
          Total Amount
        </label>
        <Input
          type="number"
          value={form.total_amount}
          onChange={(e) => onFieldChange("total_amount", e.target.value)}
        />
      </div>

      <div style={{ marginBottom: "12px" }}>
        <label style={{ display: "block", marginBottom: "8px" }}>
          Requested By
        </label>
        <SelectInput
          name="requested_by"
          options={requestedByOptions.options?.map((opt: any) => ({
            label: opt.name,
            value: opt.value,
          }))}
          loading={requestedByOptions.loading}
          onSearch={requestedByOptions.onSearch}
          value={form.requested_by}
          onChange={(value) => onFieldChange("requested_by", value)}
          placeholder="Search for a user"
        />
      </div>

      <div>
        <label style={{ display: "block", marginBottom: "8px" }}>
          Comments
        </label>
        <TextArea
          value={form.action_comment}
          placeholder="Enter comments"
          onChange={(e) => onFieldChange("action_comment", e.target.value)}
          style={{ marginTop: "8px" }}
        />
      </div>
    </div>
  );
};

const handleCustomAction = (action: {
  actionUrl?: string;
  actionLabel?: string;
  onAction?: any;
  showReasonField?: boolean;
  reasonFieldLabel?: string;
  apiMethod?: string;
  apiParams?: any;
  deleteMessage?: string;
  handleCustom?: (reason?: string) => void;
  modalTitle?: string;
  modalContent?:
    | React.ReactNode
    | ((setData: (data: any) => void) => React.ReactNode);
  modalOkText?: string;
  modalOkType?: "primary" | "danger" | "dashed" | "link" | "text" | "default";
  modalCancelText?: string;
  modalWidth?: number;
  onModalOk?: (data?: any) => void | Promise<void>;
}) => {
  const {
    actionUrl = "",
    actionLabel = "",
    onAction,
    showReasonField = false,
    reasonFieldLabel = "Provide Reason",
    apiMethod = "POST",
    apiParams,
    deleteMessage = "",
    handleCustom = () => {},
    modalTitle,
    modalContent,
    modalOkText,
    modalOkType,
    modalCancelText,
    modalWidth,
    onModalOk,
  } = action;

  // If custom modal properties are provided, use them
  if (modalContent || onModalOk) {
    let modalData: any = {};

    const content =
      typeof modalContent === "function"
        ? modalContent((data) => {
            modalData = data;
          })
        : modalContent;

    Modal.confirm({
      title: modalTitle || "Confirm Action",
      content: content,
      okText: modalOkText || "Confirm",
      okType: modalOkType || "primary",
      cancelText: modalCancelText || "Cancel",
      width: modalWidth || 520,
      onOk: async () => {
        return new Promise<void>((resolve, reject) => {
          try {
            if (onModalOk) {
              const result = onModalOk(modalData);
              if (result instanceof Promise) {
                result.then(resolve).catch(reject);
              } else {
                resolve();
              }
            } else if (handleCustom) {
              handleCustom(modalData);
              resolve();
            } else {
              resolve();
            }
          } catch (error) {
            reject(error);
          }
        });
      },
    });
    return;
  }

  // Fallback to legacy behavior with showReasonField and API calls
  if (!showReasonField && !onAction) {
    handleCustom();
    return;
  }

  let customReason = "";
  Modal.confirm({
    title: modalTitle || `Confirm ${actionLabel}`,
    content: (
      <div>
        <p>
          {deleteMessage ||
            `Are you sure you want to ${actionLabel.toLowerCase()}?`}
        </p>
        {showReasonField && (
          <div>
            <label>{reasonFieldLabel}</label>
            <TextArea
              placeholder={`Enter ${reasonFieldLabel.toLowerCase()}`}
              onChange={(e) => (customReason = e.target.value)}
              style={{ marginTop: "8px" }}
            />
          </div>
        )}
      </div>
    ),
    okText: modalOkText || `Yes, ${actionLabel}`,
    okType: modalOkType || "primary",
    cancelText: modalCancelText || "Cancel",
    onOk: async () => {
      if (onAction) {
        const params =
          showReasonField && customReason
            ? { ...apiParams, reason: customReason }
            : apiParams;
        onAction({
          api: actionUrl,
          method: apiMethod,
          params,
        });
      } else if (handleCustom) {
        handleCustom(customReason || undefined);
      }
      customReason = "";
    },
  });
};

export const ActionsMenu = (
  actions: any[],
  serviceOptions: { value: string; label: string }[] = [],
  onRequestRequisition?: (url: string, message: string, params: any) => void,
) => {
  const { uploadData: onDelete, success, error } = useSet();

  useToasts({
    success,
    error,
    redirect: "reload",
    successMessage: "Operation Successfull",
  });

  return (
    <>
      {actions?.map(
        (
          {
            label,
            to,
            state,
            onClick,
            icon: Icon,
            deleteMessage = "",
            deleteUrl = "",
            custom = false,
            handleCustom = () => {},
            showReasonField = false,
            reasonFieldLabel = "Provide Reason",
            apiMethod = "POST",
            apiParams,
            fields = {},
            actionType,
            modalTitle,
            modalContent,
            modalOkText,
            modalOkType,
            modalCancelText,
            modalWidth,
            onModalOk,
            fieldName = "checker_notes",
            method = "PATCH",
          }: TActionProps,
          key: number,
        ) => (
          <Link
            to={to || ""}
            state={state}
            onClick={
              custom
                ? (e) => {
                    e.preventDefault();
                    if (actionType === "clamp") {
                      handleClampAction(
                        deleteUrl || to || "",
                        onDelete,
                        apiMethod,
                        apiParams,
                        serviceOptions,
                      );
                    } else if (actionType === "unclamp") {
                      handleUnclampAction(
                        deleteUrl || to || "",
                        onDelete,
                        apiMethod,
                        apiParams,
                      );
                    } else if (
                      showReasonField ||
                      apiParams ||
                      modalContent ||
                      onModalOk
                    ) {
                      handleCustomAction({
                        actionUrl: deleteUrl || to || "",
                        actionLabel: label,
                        onAction: onDelete,
                        showReasonField,
                        reasonFieldLabel,
                        apiMethod,
                        apiParams,
                        deleteMessage,
                        handleCustom,
                        modalTitle,
                        modalContent,
                        modalOkText,
                        modalOkType,
                        modalCancelText,
                        modalWidth,
                        onModalOk,
                      });
                    } else {
                      handleCustom && handleCustom();
                    }
                  }
                : label === REQUEST_REQUISITION
                  ? (e) => {
                      e.preventDefault();
                      onRequestRequisition?.(
                        deleteUrl,
                        deleteMessage,
                        apiParams,
                      );
                    }
                  : label === APPROVE
                    ? (e) => {
                        e.preventDefault();
                        handleApprove(
                          deleteUrl,
                          deleteMessage,
                          onDelete,
                          true,
                          fieldName,
                          method,
                        );
                      }
                    : label === REQUEST_REVERSE
                      ? (e) => {
                          e.preventDefault();
                          handleRequestReversal(
                            deleteUrl,
                            deleteMessage,
                            onDelete,
                            true,
                          );
                        }
                      : label === REJECT
                        ? (e) => {
                            e.preventDefault();
                            handleReject(
                              deleteUrl,
                              deleteMessage,
                              onDelete,
                              true,
                            );
                          }
                        : label === DELETE
                          ? (e) => {
                              e.preventDefault();
                              handleDelete(deleteUrl, deleteMessage, onDelete);
                            }
                          : label === DEACTIVATE
                            ? (e) => {
                                e.preventDefault();
                                handleDeactivate(
                                  deleteUrl,
                                  deleteMessage,
                                  onDelete,
                                  { fields, showReasonField },
                                );
                              }
                            : label === REACTIVATE
                              ? (e) => {
                                  e.preventDefault();
                                  handleReactivate(
                                    deleteUrl,
                                    deleteMessage,
                                    onDelete,
                                  );
                                }
                              : label === ACTIVATE
                                ? (e) => {
                                    e.preventDefault();
                                    handleActivate(
                                      deleteUrl,
                                      deleteMessage,
                                      onDelete,
                                    );
                                  }
                                : label === CLOSE
                                  ? (e) => {
                                      e.preventDefault();
                                      handleClose(
                                        deleteUrl,
                                        deleteMessage,
                                        onDelete,
                                      );
                                    }
                                  : onClick
            }
            key={key}
          >
            <ItemWrapper>
              {React.isValidElement(Icon)
                ? Icon
                : (Icon && <Icon />) || getIcon(label)}
              {label}
            </ItemWrapper>
          </Link>
        ),
      )}
    </>
  );
};

export const FormActions = ({
  actions,
  serviceOptions = [],
}: TFormActionsProps) => {
  const { uploadData: submit } = useSet();
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState<{
    open: boolean;
    actionUrl?: string;
    deleteMessage?: string;
    apiParams?: any;
  }>({ open: false });
  const [form, setForm] = useState({
    staff_type: "",
    total_amount: "" as string | number,
    requested_by: "",
    action_comment: "",
  });

  const openRequisition = (
    actionUrl: string,
    deleteMessage: string,
    apiParams: any,
  ) => {
    setMenuOpen(false); // close the popover so it can't swallow updates
    setForm({
      staff_type: "",
      total_amount: apiParams?.total_amount ?? "",
      requested_by: "",
      action_comment: "",
    });
    setModal({ open: true, actionUrl, deleteMessage, apiParams });
  };

  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  const submitRequisition = () => {
    submit({
      api: modal.actionUrl || "",
      method: "POST",
      params: {
        reference_number: modal.apiParams?.reference_number,
        staff_type: form.staff_type,
        total_amount: form.total_amount,
        status: REQUISITION_STATUS_PENDING,
        action_comment: form.action_comment || null,
        requested_by: form.requested_by,
      },
    });
    closeModal();
  };

  return (
    <>
      <Popover
        open={menuOpen}
        onOpenChange={setMenuOpen}
        content={<>{ActionsMenu(actions, serviceOptions, openRequisition)}</>}
        trigger="click"
        overlayStyle={{ minWidth: 150 }}
        overlayInnerStyle={{ padding: 5 }}
        placement="left"
      >
        <IconButton size="small">
          <MoreVert />
        </IconButton>
      </Popover>

      <Modal
        title="Confirm Request Requisition"
        open={modal.open}
        onCancel={closeModal}
        onOk={submitRequisition}
        okText="Yes, Submit"
        cancelText="Cancel"
        destroyOnClose
      >
        {modal.open && (
          <RequestRequisitionModalContent
            deleteMessage={modal.deleteMessage || ""}
            initialParams={modal.apiParams}
            form={form}
            onFieldChange={(f, v) => setForm((p) => ({ ...p, [f]: v }))}
          />
        )}
      </Modal>
    </>
  );
};

// export const FormActions = ({
//   actions,
//   serviceOptions = [],
// }: TFormActionsProps) => {
//   return (
//     <Popover
//       content={<>{ActionsMenu(actions, serviceOptions)}</>}
//       trigger="click"
//       overlayStyle={{ minWidth: 150 }}
//       overlayInnerStyle={{ padding: 5 }}
//       placement="left"
//     >
//       <IconButton size="small">
//         <MoreVert />
//       </IconButton>
//     </Popover>
//   );
// };
